import { Role as StandardRole } from '@customerio/hydra-permissions/roles';

/**
 * A role name: a standard role or a custom-role name string. Only standard Author/Viewer roles have
 * redaction variants; custom roles never do.
 */
export type Role = StandardRole | (string & {});

export function isStandardRole(role: Role): role is StandardRole {
  return Object.values<string>(StandardRole).includes(role);
}

const REDACTED_TO_UNREDACTED_MAP = new Map([
  [StandardRole.AuthorRedacted, StandardRole.Author],
  [StandardRole.AuthorRedactedSensitive, StandardRole.Author],
  [StandardRole.AuthorRedactedAll, StandardRole.Author],
  [StandardRole.ViewerRedacted, StandardRole.Viewer],
  [StandardRole.ViewerRedactedSensitive, StandardRole.Viewer],
  [StandardRole.ViewerRedactedAll, StandardRole.Viewer],
]);

export function isRoleRedacted(role?: Role | null): boolean {
  if (role == null) {
    return false;
  }
  // Custom roles can't be redacted, only standard roles can be.
  if (!isStandardRole(role)) {
    return false;
  }
  return REDACTED_TO_UNREDACTED_MAP.has(role);
}

export const RedactionLevel = {
  None: 'none',
  Sensitive: 'sensitive',
  All: 'all',
} as const;

export type RedactionLevel = (typeof RedactionLevel)[keyof typeof RedactionLevel];

export function isValidRedactionLevel(value: unknown): value is RedactionLevel {
  if (typeof value !== 'string') {
    return false;
  }
  return Object.values<string>(RedactionLevel).includes(value);
}

/** The base (non-redacted) standard role for a redaction variant; other roles pass through. */
export function stripRedactionFromRole(role: Role): Role {
  // Custom roles can't be redacted, so return as-is.
  if (!isStandardRole(role)) {
    return role;
  }
  return REDACTED_TO_UNREDACTED_MAP.get(role) ?? role;
}

/** The concrete DB role name for a base standard role at a given redaction level. */
export function roleNameWithRedactionLevel(role: Role, redactionLevel: RedactionLevel): Role {
  // Custom roles can't be redacted, so return as-is.
  if (!isStandardRole(role)) {
    return role;
  }

  switch (role) {
    case StandardRole.Admin:
    case StandardRole.Manager:
      return role;
    case StandardRole.Author:
    case StandardRole.AuthorRedacted:
    case StandardRole.AuthorRedactedSensitive:
    case StandardRole.AuthorRedactedAll:
      if (redactionLevel === RedactionLevel.None) {
        return stripRedactionFromRole(role);
      }
      if (redactionLevel === RedactionLevel.Sensitive) {
        return StandardRole.AuthorRedactedSensitive;
      }
      return StandardRole.AuthorRedactedAll;
    case StandardRole.Viewer:
    case StandardRole.ViewerRedacted:
    case StandardRole.ViewerRedactedSensitive:
    case StandardRole.ViewerRedactedAll:
      if (redactionLevel === RedactionLevel.None) {
        return stripRedactionFromRole(role);
      }
      if (redactionLevel === RedactionLevel.Sensitive) {
        return StandardRole.ViewerRedactedSensitive;
      }
      return StandardRole.ViewerRedactedAll;
    default:
      return role;
  }
}

/** The redaction level a role currently represents. */
export function redactionLevelForRole(role: Role): RedactionLevel {
  // Custom roles can't be redacted, so always return None.
  if (!isStandardRole(role)) {
    return RedactionLevel.None;
  }

  switch (role) {
    case StandardRole.AuthorRedacted:
    case StandardRole.AuthorRedactedAll:
    case StandardRole.ViewerRedacted:
    case StandardRole.ViewerRedactedAll:
      return RedactionLevel.All;
    case StandardRole.AuthorRedactedSensitive:
    case StandardRole.ViewerRedactedSensitive:
      return RedactionLevel.Sensitive;
    default:
      return RedactionLevel.None;
  }
}
